import { FunctionComponent } from 'react';
import { useGlobalContext } from 'components/common/GlobalContext/GlobalContext';
import { HStack, VStack, Text } from '@montel/montelpro-shared-components-v2';
import { getProductConfig } from 'domain/product/productMap';
import { Subtle } from './styles';
import { PermissionSetDelta } from './interfaces';
import { DeltaValue } from './DeltaValue';

export const PermissionSetRows: FunctionComponent<{
	permissions: Record<string, PermissionSetDelta>;
}> = ({ permissions }) => {
	const allAppIds = Object.keys(permissions || {});
	const { getPlanByCode, getAreaByCode, getAddonByCode } = useGlobalContext();

	const mapCodesToNames = (
		appId: string,
		codes: string[] | string | undefined,
		getter: (appId: string, code: string) => any,
	): string | string[] | undefined => {
		if (!codes) return codes;

		if (Array.isArray(codes)) {
			return codes.map((code) => getter(appId, code)?.name || code);
		}

		return (
			getter(appId, codes)?.displayName ||
			getter(appId, codes)?.name ||
			codes
		);
	};

	return (
		<VStack gap="lg" fullWidth>
			{allAppIds.map((appId) => {
				const appPerms = permissions[appId];
				const productConfig = getProductConfig(appId);
				const displayName = productConfig?.name || appId;

				return (
					<VStack key={appId} gap="sm" fullWidth>
						<HStack wrap="nowrap">
							<Text>
								{displayName}{' '}
								<Subtle>{`(${appPerms.operation})`}</Subtle>
							</Text>
						</HStack>
						<VStack gap="xs" fullWidth>
							<DeltaValue
								from={mapCodesToNames(
									appId,
									appPerms.plan?.from,
									getPlanByCode,
								)}
								to={mapCodesToNames(
									appId,
									appPerms.plan?.to,
									getPlanByCode,
								)}
								label="Plan"
							/>
							<DeltaValue
								from={mapCodesToNames(
									appId,
									appPerms.areas?.from,
									getAreaByCode,
								)}
								to={mapCodesToNames(
									appId,
									appPerms.areas?.to,
									getAreaByCode,
								)}
								label="Areas"
							/>
							<DeltaValue
								from={mapCodesToNames(
									appId,
									appPerms.addons?.from,
									getAddonByCode,
								)}
								to={mapCodesToNames(
									appId,
									appPerms.addons?.to,
									getAddonByCode,
								)}
								label="Addons"
							/>
						</VStack>
					</VStack>
				);
			})}
		</VStack>
	);
};
